import { Home, Briefcase, Scale, Shield, ArrowRight, Check } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useStaggeredFadeIn } from "@/hooks/useFadeIn";
import LocalLink from "@/components/LocalLink";

const ServicesSection = () => {
  const { t } = useLanguage();
  const { ref, isVisible } = useStaggeredFadeIn(4);

  const cards = [
    {
      icon: Home,
      label: t("services.family.title"),
      descriptionKey: "services.family.description",
      items: ["services.family.item1", "services.family.item2", "services.family.item3"],
      link: "/services/family-immigration",
    },
    {
      icon: Briefcase,
      label: t("services.business.title"),
      descriptionKey: "services.business.description",
      items: ["services.business.item1", "services.business.item2", "services.business.item3"],
      link: "/contact",
    },
    {
      icon: Scale,
      label: t("services.courtroom.title"),
      descriptionKey: "services.courtroom.description",
      items: ["services.courtroom.item1", "services.courtroom.item2", "services.courtroom.item3"],
      link: "/services/deportation",
    },
    {
      icon: Shield,
      label: t("services.criminal.title"),
      descriptionKey: "services.criminal.description",
      items: ["services.criminal.item1", "services.criminal.item2"],
      link: "/services/dui-dwi-defense",
    },
  ];

  return (
    <section id="services" className="py-16 md:py-20 bg-secondary/30 scroll-mt-header">
      <div className="container-custom">
        {/* Header */}
        <div
          ref={ref}
          className={`text-center max-w-2xl mx-auto mb-10 fade-up ${isVisible ? "visible" : ""}`}
        >
          <span className="eyebrow-text">{t("services.label")}</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mt-3 mb-4">
            {t("services.title")}
          </h2>
          <p className="text-muted-foreground">{t("services.subtitle")}</p>
        </div>

        {/* 2x2 Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {cards.map((card, i) => {
            const Icon = card.icon;
            return (
              <LocalLink
                key={i}
                to={card.link}
                aria-label={`${card.label} — ${t("services.learnMore")}`}
                className={`group relative rounded-2xl p-7 md:p-8 transition-all duration-400 ease-out
                  bg-[hsl(213_50%_13%)] border border-transparent
                  hover:border-accent hover:-translate-y-1 hover:shadow-[0_20px_50px_-15px_rgba(212,175,55,0.15)]
                  fade-up ${isVisible ? "visible" : ""}`}
                style={{ transitionDelay: isVisible ? `${i * 100}ms` : "0ms" }}
              >
                {/* Icon */}
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4
                  group-hover:bg-accent/20 group-hover:shadow-[0_0_20px_rgba(212,175,55,0.2)] transition-all duration-300">
                  <Icon className="w-6 h-6 text-accent" />
                </div>

                {/* Title */}
                <h2 className="text-xl font-heading font-bold text-white mb-3">
                  {card.label}
                </h2>

                {/* Checklist */}
                <ul className="space-y-2 mb-5">
                  {card.items.map((key) => (
                    <li key={key} className="flex items-center gap-2.5 text-sm text-white/70">
                      <Check className="w-4 h-4 text-accent flex-shrink-0" />
                      {t(key)}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent/80
                  group-hover:text-accent transition-colors">
                  {t("services.learnMore")}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </span>
              </LocalLink>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
